import React, { useState, useCallback } from "react";
import { Sidebar } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
// import Chatbot from "@/components/Chatbot";
// import CallingBot from "@/components/Callingbot";
import BotLauncher from "@/components/BotLauncher";
import {
  Search,
  HelpCircle,
  FileText,
  Video,
  MessageCircle,
  Mail,
  Phone,
  ExternalLink,
  BookOpen,
  Zap,
  CreditCard,
  BarChart3,
  FolderKanban,
} from "lucide-react";
import { PageHeader } from "@/components/Header";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import mylistiqGuide from "../../public/guides/mylistiqguide1.pdf";

const Help = () => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("getting-started");
  const [isGuideModalOpen, setIsGuideModalOpen] = useState(false);
  const [guideLoading, setGuideLoading] = useState(true);
  const [guideKey, setGuideKey] = useState(0);
  const [mobileMenuToggle, setMobileMenuToggle] = useState<(() => void) | null>(null);

  const categories = [
    { id: "getting-started", name: "Getting Started", icon: HelpCircle },
    { id: "billing", name: "Account & Billing", icon: CreditCard },
    { id: "features", name: "Features & Usage", icon: Zap },
    { id: "security", name: "Security & Privacy", icon: BarChart3 },
    { id: "builder", name: "The Launch Builder", icon: FileText },
    { id: "support", name: "Support & Contact", icon: MessageCircle },
  ];

  const faqs = [
    {
      id: 1,
      category: "getting-started",
      question: "What is MyListIQ?",
      answer:
        "MyListIQ is a web-based marketing platform built for real estate agents, property marketers, and small businesses. It helps you list smarter and market better by providing tools for importing listing data, generating AI-powered marketing content, using professional templates, and tracking your launches — all in one place.",
    },
    {
      id: 2,
      category: "getting-started",
      question: "Who is MyListIQ designed for?",
      answer:
        "MyListIQ is ideal for real estate agents, property marketers, and small business owners who want to streamline their listing promotions and marketing launches without needing advanced technical skills.",
    },
    {
      id: 3,
      category: "getting-started",
      question: "How do I create an account?",
      answer:
        "Visit mylistiq.com and click the 'Get Started' or 'Go to Dashboard' button. Choose a plan that fits your needs, complete the signup process, and you'll be ready to start creating your first Launch.",
    },
    {
      id: 4,
      category: "getting-started",
      question: "What is a 'Launch' on MyListIQ?",
      answer:
        "A Launch is your marketing campaign (referenced as launch) unit on MyListIQ. Each Launch represents a listing or campaign you set up — including its imported data, AI-generated content, templates, and tracking. Depending on your plan, you can run one or more Launches simultaneously.",
    },
    {
      id: 5,
      category: "getting-started",
      question: "What is 'My Hub'?",
      answer:
        "My Hub is your contact management center within MyListIQ. It allows you to store, organize, and manage the contacts associated with your marketing efforts. Additional Hubs can be added to your plan if needed.",
    },
    {
      id: 6,
      category: "getting-started",
      question: "Can I try MyListIQ before committing to a subscription?",
      answer: "Yes, one-time and 7-day free trial.",
    },
    {
      id: 7,
      category: "billing",
      question: "What plans does MyListIQ offer?",
      answer:
        "MyListIQ offers four plans: One-Time ($25.00, single Launch), Starter ($19/month, 1 Launch), Pro ($39/month, 3 Launches), and Pro+ ($59/month, 5 Launches). All plans include access to templates, AI tools, import features, Hub contacts, and tracking capabilities.",
    },
    {
      id: 8,
      category: "billing",
      question: "What is included in the One-Time plan?",
      answer:
        "The One-Time plan costs $25.00 and includes 1 Launch (with additional Launches at $10 each), 1 Hub ($10 per additional Hub), templates, Import to Launch, My Hub contacts, AI Generated Description & Subject Line, AI Photo Enhancement, AI Insight & Analytics, Standard Tracker (email notification), and Enhanced Tracker (SMS notification).",
    },
    {
      id: 9,
      category: "billing",
      question: "What is the difference between the Pro and Pro+ plans?",
      answer:
        "The Pro plan ($39/month) includes 3 Launches with additional Launches at $5 each and $5 per additional Hub. The Pro+ plan ($59/month) includes 5 Launches with the same $5 add-on pricing. Both plans share identical features — the difference is the number of included Launches.",
    },
    {
      id: 10,
      category: "billing",
      question: "How much does it cost to add extra Launches or Hubs?",
      answer:
        "On the One-Time and Starter plans, additional Launches and Hubs cost $10 each. On the Pro and Pro+ plans, additional Launches and Hubs are $5 each — a better value for higher-volume users.",
    },
    {
      id: 11,
      category: "billing",
      question: "Can I upgrade or downgrade my plan at any time?",
      answer:
        "Yes, you can change your plan at any time from your account dashboard. If you have questions about plan changes or billing adjustments, reach out to the support team at support@mylistiq.com for assistance.",
    },
    {
      id: 12,
      category: "billing",
      question: "How do I cancel my subscription?",
      answer:
        "You can manage your subscription from your account settings. To cancel, navigate to your billing section in the dashboard or contact support at support@mylistiq.com. Cancellations take effect at the end of your current billing cycle.",
    },
    {
      id: 13,
      category: "billing",
      question: "Is there a refund policy?",
      answer:
        "For refund inquiries, please contact the MyListIQ support team at support@mylistiq.com. Our team will review your request and work with you to find the best resolution.",
    },
    {
      id: 14,
      category: "features",
      question: "What is the 'Import to Launch' feature?",
      answer:
        "Import to Launch allows you to bring in your property listing data directly into a Launch on the platform. This streamlines the setup process so you don't have to manually enter listing details — saving you time and reducing errors.",
    },
    {
      id: 15,
      category: "features",
      question: "How does AI Generated Description & Subject Line work?",
      answer:
        "MyListIQ uses artificial intelligence to automatically craft compelling property descriptions and email subject lines based on your listing data. This helps you produce professional-quality marketing copy instantly.",
    },
    {
      id: 16,
      category: "features",
      question: "What is AI Photo Enhancement?",
      answer:
        "AI Photo Enhancement uses intelligent image processing to improve the quality and visual appeal of your listing photos. It automatically enhances brightness, clarity, and overall presentation.",
    },
    {
      id: 17,
      category: "features",
      question: "What are AI Insight & Analytics?",
      answer:
        "AI Insight & Analytics provides intelligent reporting on how your launches are performing. You can track engagement, see which content resonates with your audience, and make data-informed decisions to optimize future Launches.",
    },
    {
      id: 18,
      category: "features",
      question:
        "What is the difference between Standard Tracker and Enhanced Tracker?",
      answer:
        "The Standard Tracker sends email notifications when recipients interact with your Launch. The Enhanced Tracker also sends SMS notifications, so you're alerted in real time.",
    },
    {
      id: 19,
      category: "features",
      question: "What types of templates are available?",
      answer:
        "MyListIQ provides a library of professionally designed marketing templates tailored for real estate listings and promotional launches. Templates are ready to use and can be customized to reflect your branding and listing details.",
    },
    {
      id: 20,
      category: "features",
      question: "Can I manage multiple listings at the same time?",
      answer:
        "Yes, depending on your plan. The Starter plan supports 1 active Launch, Pro supports 3, and Pro+ supports 5. You can also add more Launches to any plan for an additional fee.",
    },

    {
      id: 40,
      category: "features",
      question: "What is the rollover feature?",
      answer:
        "The rollover feature allows unused Launch credits to carry forward for a limited time based on the selected plan.",
      answer2:
        "This feature is available only on the Pro and Pro+ plans.",
    },
    {
      id: 41,
      category: "features",
      question: "How is the rollover accessible ?",
      answer:
        "Pro Plan: Unused credits roll over for 1 month, with a maximum accumulation of up to 3 Launches.",

      answer2:
        "Pro+ Plan: Unused credits roll over for 2 months, with a maximum accumulation of up to 10 Launches.",
      answer3:
        "Starter and One-Time plans do not include rollover credits.",
    },
    {
      id: 42,
      category: "features",
      question: "How does the free trial work?",

      answer:
        "The Pro, Pro+, and Starter plans include a 7-day free trial with up to 2 free Launches for My Hubs.",

      answer2: "During the free trial:",

      bullet1:
        "Only My Hubs groups can be used for Launches.",

      bullet2:
        "Users can send up to 2 free Launches.",

      bullet3:
        "Core platform features remain accessible based on the selected plan.",

      answer3:
        "After the 7-day free trial ends, Stripe will automatically charge the subscription amount, and the remaining Launch credits included in the selected plan will be added to the account.",
    },

    {
      id: 43,
      category: "features",
      question: "How can I cancel a scheduled launch?",
      answer:
        "You can cancel your scheduled launch directly from the platform by following these steps:",
      bullet1: "Go to Launch Management from the main menu.",
      bullet2: "Find the scheduled launch you want to cancel.",
      bullet3: "Click the Cancel button.",
      bullet4:
        "You will see the full details of the scheduled launch including any extra hub charges that were added.",
      bullet5:
        "Once you confirm the cancellation, the equivalent credits will be added back to your account based on the amount paid.",
      answer2:
        "No refunds are issued. Cancellations are credited back to your account and can be used toward future launches.",
    },

    {
      id: 44,
      category: "features",
      question: "How is the Open Rate and Click Rate calculated?",
      answer:
        "Open Rate and Click Rate are key metrics available in your MyListIQ Analytics and LaunchIQ management pages.",
      bullet1: "Open Rate is the percentage of delivered emails that were opened by recipients.",
      bullet2: "Click Rate is the percentage of delivered emails where a recipient clicked a link.",
      answer2: "Open Rate formula:",
      answer3: "(Emails Opened ÷ Emails Delivered) × 100",
      answer4: "Click Rate formula:",
      answer5: "(Unique Clicks ÷ Emails Delivered) × 100",
      answer6:
        "Note: Only unique clicks are counted. If a recipient clicks multiple times, it is counted as one click.",
    },
    {
      id: 21,
      category: "security",
      question: "Is my data safe on MyListIQ?",
      answer:
        "Yes. MyListIQ is committed to protecting your data. The platform uses industry-standard security practices to safeguard your account information, contact data, and launch content.",
    },
    {
      id: 22,
      category: "security",
      question: "Who has access to my contacts in My Hub?",
      answer:
        "Only you and authorized users on your account have access to your Hub contacts. MyListIQ does not share or sell your contact list to external parties.",
    },
    {
      id: 23,
      category: "security",
      question: "How does MyListIQ handle my listing and marketing data?",
      answer:
        "Your listing data and marketing assets are stored securely and used solely to power your launches within the platform. For full details, review the MyListIQ Privacy Policy.",
    },
    {
      id: 24,
      category: "security",
      question: "Can I delete my account and data?",
      answer:
        "Yes. If you wish to delete your account and all associated data, please contact the support team at support@mylistiq.com. The team will guide you through the process.",
    },
    {
      id: 25,
      category: "security",
      question: "Is my payment information secure?",
      answer:
        "MyListIQ uses trusted, PCI-compliant payment processing to handle all billing transactions. Your full payment card details are never stored directly on MyListIQ's servers.",
    },
    {
      id: 45,
      category: "security",
      question: "How do I unsubscribe from MyListIQ emails?",
      answer:
        "Click the unsubscribe link in any MyListIQ email and follow the unsubscribe process. If the unsubscribe page or confirmation modal does not appear, email support@mylistiq.com with the subject line 'UNSUBSCRIBE' and include the email address you wish to remove.",
    },
 
    {
      id: 26,
      category: "builder",
      question: "What is the Launch Builder?",
      answer:
        "The Launch Builder is MyListIQ's guided workflow that walks you through creating and sending a complete marketing launch for your listing. It has 9 simple steps — from choosing your launch type to seeing your results.",
    },
    {
      id: 27,
      category: "builder",
      question: "Step 1: Pick Your Launch — What does this mean?",
      answer:
        "Choose the type of launch you want to create (e.g., new listing, open house). Selecting the right launch type ensures templates and content are tailored to your goal.",
    },
    {
      id: 28,
      category: "builder",
      question:
        "Step 2: Tell Us About Your Launch — What information do I need to provide?",
      answer:
        "Add your property details: address, description, price, features, etc. You can also use Import to Launch to pull listing data automatically.",
    },
    {
      id: 29,
      category: "builder",
      question: "Step 3: Pick Your Look — How do I choose a template?",
      answer:
        "Browse the template library and select one that fits your listing and style. Templates are pre-built for real estate marketing, so you can choose one and move on quickly.",
    },
    {
      id: 30,
      category: "builder",
      question: "Step 4: Preview Template — What can I review here?",
      answer:
        "Before committing, you get a full preview of how your marketing piece will look with your listing details applied to the chosen template.",
    },
    {
      id: 31,
      category: "builder",
      question:
        "Step 5: Choose Your Hub — What is a Hub and who are recipient groups?",
      answer:
        "Your Hub is your contact database on MyListIQ. In this step, you select which Hub (contact group) you want to send your launch to.",
    },
    {
      id: 32,
      category: "builder",
      question: "Step 6: Schedule Your Launch — When will my launch be sent?",
      answer:
        "Here you choose when your launch goes out. You can send it immediately or schedule it for a specific date and time.",
    },
    {
      id: 33,
      category: "builder",
      question: "Step 7: Complete Payment — What happens at this step?",
      answer:
        "If your launch requires a payment, you'll enter your payment details here. For launches within your plan's allowance, this step confirms there is no additional charge.",
    },
    {
      id: 34,
      category: "builder",
      question: "Step 8: You're All Set to Launch — What are the final checks?",
      answer:
        "This is your last review before your launch goes live. MyListIQ does a final check to make sure all your details, design, recipients, and schedule are in order.",
    },
    {
      id: 35,
      category: "builder",
      question: "Step 9: Launch Successful! — What can I see in my results?",
      answer:
        "Congratulations — your launch is live! You'll see a confirmation and start receiving tracking data. Depending on your plan, you'll get email and/or SMS notifications as recipients engage.",
    },
    {
      id: 36,
      category: "builder",
      question: "Can I go back and edit a step during the Launch Builder?",
      answer:
        "Yes, you can navigate back to previous steps before your launch is finalized. Once a Launch is sent, the content cannot be changed, but you can always create a new Launch.",
    },
    {
      id: 37,
      category: "support",
      question: "How do I contact support?",
      answer:
        "You can reach the MyListIQ support team via email at support@mylistiq.com or call us at +1 (321) 519-4247. The team is available Monday through Friday during business hours (9:00 AM–6:00 PM EST/EDT), with limited availability on Saturdays.",
    },
    {
      id: 38,
      category: "support",
      question: "Where is MyListIQ located?",
      answer:
        "MyListIQ is based in Orlando, Florida. The mailing address is: 5366 Central Florida Pkwy #1015, Orlando, FL 32821.",
    },
    {
      id: 39,
      category: "support",
      question: "What should I do if I encounter a technical issue?",
      answer:
        "If you experience any technical difficulties, first try refreshing your browser or clearing your cache. If the problem persists, contact support at support@mylistiq.com with a description of the issue and any relevant screenshots.",
    },
  ];

  const quickLinks = [
    { title: "Getting Started Guide", icon: BookOpen, url: "guide" },
    { title: "Video Tutorials", icon: Video, url: "video" },
    { title: "Launch Management", icon: FileText, url: "/launches" },
    { title: "Launch Builder", icon: Zap, url: "/builder" },
  ];

  const filteredFaqs =
    selectedCategory === "all"
      ? faqs.filter(
          (faq) =>
            faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : faqs.filter(
          (faq) =>
            faq.category === selectedCategory &&
            (faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
              faq.answer.toLowerCase().includes(searchQuery.toLowerCase()))
        );

  const activeCategoryName = categories.find((c) => c.id === selectedCategory)?.name ?? "";

  const handleRegisterMobileToggle = useCallback((fn: () => void) => {
    setMobileMenuToggle(() => fn);
  }, []);

  return (
    <div className="flex min-h-screen bg-white">
      <Sidebar registerMobileToggle={handleRegisterMobileToggle} />

      <main className="flex-1 flex flex-col min-w-0">
        <PageHeader
          mobileMenuOffset={true}
          onMenuClick={mobileMenuToggle ?? undefined}
        />

        <div className="flex-1 overflow-y-auto">
          <div className="py-6 sm:py-8">
            <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8">

              {/* ── Search ─────────────────────────────────── */}
              <div className="max-w-2xl mx-auto mb-6 sm:mb-8">
                <div className="relative">
                  <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4 sm:h-5 sm:w-5" />
                  <Input
                    placeholder="Search for help articles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 sm:pl-12 py-2.5 sm:py-3 text-base sm:text-lg"
                  />
                </div>
              </div>

              {/* ── Quick Links ────────────────────────────── */}
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
                {quickLinks.map((link) => (
                  <Card
                    key={link.title}
                    onClick={() => {
                      if (link.url === "video") {
                        setIsVideoLoading(true);
                        setIsVideoModalOpen(true);
                      } else if (link.url === "guide") {
                        setGuideLoading(true);
                        setGuideKey((prev) => prev + 1);
                        setIsGuideModalOpen(true);
                      } else if (link.url) {
                        window.location.href = link.url;
                      }
                    }}
                    className="shadow-card hover:shadow-elevated transition-smooth cursor-pointer group h-full"
                  >
                    <CardContent className="p-4 sm:p-6 text-center flex flex-col items-center justify-center h-full gap-2 sm:gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                        <link.icon className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                      </div>
                      <h3 className="font-medium text-black text-xs sm:text-sm leading-tight">
                        {link.title}
                      </h3>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* ── Main Content Grid ──────────────────────── */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">

                {/* ── Categories sidebar ─────────────────── */}
                {/* Mobile: horizontal scrollable pill row */}
                <div className="lg:col-span-1">

                  {/* Mobile pill row (hidden on lg+) */}
                  <div className="lg:hidden flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
                    {categories.map((category) => {
                      const Icon = category.icon;
                      const isActive = selectedCategory === category.id;
                      return (
                        <button
                          key={category.id}
                          onClick={() => setSelectedCategory(category.id)}
                          className={`
                            flex-shrink-0 flex items-center gap-1.5
                            px-3 py-2 rounded-full text-xs font-medium
                            transition-all whitespace-nowrap border
                            ${isActive
                              ? "bg-blue-500 text-white border-blue-500 shadow-md"
                              : "bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600"
                            }
                          `}
                        >
                          <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                          {category.name}
                        </button>
                      );
                    })}
                  </div>

                  {/* Desktop sidebar card (hidden on mobile) */}
                  <Card className="shadow-card hidden lg:block sticky top-4">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-black text-base">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-100 flex-shrink-0">
                          <FolderKanban className="w-3.5 h-3.5 text-red-600" />
                        </span>
                        Categories
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1 pt-0">
                      {categories.map((category) => {
                        const isActive = selectedCategory === category.id;
                        return (
                          <button
                            key={category.id}
                            onClick={() => setSelectedCategory(category.id)}
                            className={`
                              w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                              transition-all text-left text-sm
                              ${isActive
                                ? "bg-blue-500 text-white shadow-md"
                                : "hover:bg-gray-100 text-gray-700"
                              }
                            `}
                          >
                            <div className={`p-1.5 rounded-md flex-shrink-0 ${isActive ? "bg-white/20" : "bg-blue-100"}`}>
                              <category.icon className={`h-3.5 w-3.5 ${isActive ? "text-white" : "text-blue-600"}`} />
                            </div>
                            <span className="font-medium">{category.name}</span>
                          </button>
                        );
                      })}
                    </CardContent>
                  </Card>
                </div>

                {/* ── FAQ content ────────────────────────── */}
                <div className="lg:col-span-3">
                  <Card className="shadow-card">
                    <CardHeader className="pb-3 sm:pb-4">
                      {/* Header row — fully responsive */}
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">

                        {/* Left: icon + title + badge — all on one line */}
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-red-100 flex-shrink-0">
                            <HelpCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600" />
                          </span>
                          <h2 className="text-sm sm:text-base font-semibold text-black leading-snug whitespace-nowrap">
                            Frequently Asked Questions
                          </h2>
                          {selectedCategory !== "getting-started" && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 whitespace-nowrap flex-shrink-0">
                              {activeCategoryName}
                            </Badge>
                          )}
                        </div>

                        {/* Right: result count */}
                        <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap flex-shrink-0">
                          {filteredFaqs.length} result{filteredFaqs.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </CardHeader>

                    <CardContent className="pt-0">
                      {filteredFaqs.length > 0 ? (
                        <Accordion type="single" collapsible className="space-y-2">
                          {filteredFaqs.map((faq) => (
                            <AccordionItem
                              key={faq.id}
                              value={faq.id.toString()}
                              className="border border-border rounded-lg px-3 sm:px-4 data-[state=open]:border-blue-500 data-[state=open]:bg-gradient-to-r data-[state=open]:from-blue-50 data-[state=open]:to-indigo-50"
                            >
                              <AccordionTrigger className="text-left hover:no-underline py-3 sm:py-4">
                                <div className="flex items-start gap-2.5 sm:gap-3">
                                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <HelpCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-blue-600" />
                                  </div>
                                  <span className="font-medium text-black text-sm sm:text-base leading-snug">
                                    {faq.question}
                                  </span>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className="pt-1 pb-3 sm:pt-2 sm:pb-4 pl-8 sm:pl-9 text-muted-foreground text-sm sm:text-base leading-relaxed">
                                <div className="space-y-3">
                                  {faq.answer && <p>{faq.answer}</p>}

                                  {(faq.bullet1 ||
                                    faq.bullet2 ||
                                    faq.bullet3 ||
                                    faq.bullet4 ||
                                    faq.bullet5) && (
                                    <ul className="list-disc pl-5 space-y-1">
                                      {faq.bullet1 && <li>{faq.bullet1}</li>}
                                      {faq.bullet2 && <li>{faq.bullet2}</li>}
                                      {faq.bullet3 && <li>{faq.bullet3}</li>}
                                      {faq.bullet4 && <li>{faq.bullet4}</li>}
                                      {faq.bullet5 && <li>{faq.bullet5}</li>}
                                    </ul>
                                  )}

                                  {faq.answer2 && <p>{faq.answer2}</p>}
                                  {faq.answer3 && <p>{faq.answer3}</p>}
                                  {faq.answer4 && <p>{faq.answer4}</p>}
                                  {faq.answer5 && <p>{faq.answer5}</p>}
                                  {faq.answer6 && <p>{faq.answer6}</p>}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      ) : (
                        <div className="text-center py-10 sm:py-12">
                          <HelpCircle className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground mx-auto mb-3 sm:mb-4" />
                          <h3 className="text-base sm:text-lg font-medium text-black mb-2">
                            No results found
                          </h3>
                          <p className="text-sm sm:text-base text-gray-500">
                            Try adjusting your search or selecting a different category.
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* ── Video Modal ────────────────────────────────── */}
        <Dialog
          open={isVideoModalOpen}
          onOpenChange={(open) => {
            setIsVideoModalOpen(open);
            if (!open) setIsVideoLoading(true);
          }}
        >
          <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] p-0 overflow-y-auto overflow-x-hidden bg-white rounded-2xl shadow-2xl">
            <DialogHeader className="sticky top-0 z-10 px-4 sm:px-8 pt-5 sm:pt-8 pb-3 sm:pb-5 text-center bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
              <DialogTitle className="text-lg sm:text-2xl md:text-3xl font-bold text-black">
                Video Tutorials
              </DialogTitle>
              <p className="mt-1 text-sm sm:text-base text-gray-600">
                Learn how to use MyListIQ
              </p>
            </DialogHeader>

            <div className="relative w-full bg-black flex-shrink-0">
              {isVideoLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black z-10 min-h-[200px] sm:min-h-[300px]">
                  <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-4 border-red-600 border-t-transparent mb-3" />
                  <p className="text-white text-xs sm:text-sm">Loading video...</p>
                </div>
              )}
              <video
                src="/videos/video.mp4"
                controls
                autoPlay
                muted={false}
                className={`w-full aspect-video block transition-opacity duration-300 ${isVideoLoading ? "opacity-0" : "opacity-100"}`}
                poster="/videos/thumbnail.jpg"
                preload="auto"
                onCanPlay={() => setIsVideoLoading(false)}
                onLoadedData={() => setIsVideoLoading(false)}
              >
                <source src="/videos/video.mp4" type="video/mp4" />
                Your browser does not support video playback.
              </video>
            </div>

            <div className="px-4 sm:px-8 py-4 sm:py-6 text-center bg-gray-50 border-t border-gray-100 flex-shrink-0">
              {/* <p className="text-xs sm:text-sm text-gray-600">
                Need personal help?{" "}
                <span className="font-medium text-red-600 cursor-pointer hover:underline">
                  Chat with us
                </span>{" "}
                or book a free demo.
              </p> */}
            </div>
          </DialogContent>
        </Dialog>

        {/* ── Guide Modal ────────────────────────────────── */}
        <Dialog open={isGuideModalOpen} onOpenChange={setIsGuideModalOpen}>
          <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] p-0 overflow-y-auto overflow-x-hidden bg-white rounded-2xl shadow-2xl">
            <DialogHeader className="sticky top-0 z-10 px-4 sm:px-8 pt-5 sm:pt-8 pb-3 sm:pb-5 text-center bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
              <DialogTitle className="text-lg sm:text-2xl md:text-3xl font-bold text-black">
                Getting Started Guide
              </DialogTitle>
              <p className="mt-1 text-sm sm:text-base text-gray-600">
                Follow our step-by-step onboarding guide
              </p>
            </DialogHeader>

            <div className="relative w-full min-h-[280px] sm:min-h-[450px] md:min-h-[640px] flex-shrink-0">
              {guideLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 z-20">
                  <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-4 border-red-600 border-t-transparent mb-3" />
                  <p className="text-gray-600 text-xs sm:text-sm">Loading guide...</p>
                </div>
              )}
              <iframe
                key={guideKey}
                src={mylistiqGuide}
                width="100%"
                height="100%"
                allow="fullscreen"
                className="absolute inset-0 w-full h-full"
                style={{ border: 0 }}
                onLoad={() => setGuideLoading(false)}
              />
            </div>

            <div className="px-4 sm:px-8 py-4 sm:py-6 text-center bg-gray-50 border-t border-gray-100 flex-shrink-0">
              {/* <p className="text-xs sm:text-sm text-gray-600">
                Need more help?{" "}
                <span className="font-medium text-red-600 cursor-pointer hover:underline">
                  Chat with us
                </span>{" "}
                anytime.
              </p> */}
            </div>
          </DialogContent>
        </Dialog>

        <BotLauncher />
      </main>
    </div>
  );
};

export default Help;